import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi } from '../../api/finance';
import { ApiError } from '../../api/client';
import { useToast } from '../../components/Toast';

export function FinanceSettingsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({ queryKey: ['finance-settings'], queryFn: () => financeApi.listSettings() });
  const accountsQuery = useQuery({ queryKey: ['finance-accounts'], queryFn: () => financeApi.listAccounts() });

  const getSetting = (key: string) => settingsQuery.data?.data.find((s) => s.setting_key === key)?.setting_value ?? '';

  const { register, handleSubmit } = useForm<{
    defaultCurrency: string; fiscalYearStartMonth: string;
    defaultPersonnelExpenseAccountId: string; defaultPayrollLiabilityAccountId: string; defaultBankAccountId: string;
    defaultAccountsPayableAccountId: string; defaultAccountsReceivableAccountId: string;
  }>({
    values: settingsQuery.data ? {
      defaultCurrency: getSetting('default_currency') || 'NGN',
      fiscalYearStartMonth: getSetting('fiscal_year_start_month') || '1',
      defaultPersonnelExpenseAccountId: getSetting('default_personnel_expense_account_id'),
      defaultPayrollLiabilityAccountId: getSetting('default_payroll_liability_account_id'),
      defaultBankAccountId: getSetting('default_bank_account_id'),
      defaultAccountsPayableAccountId: getSetting('default_accounts_payable_account_id'),
      defaultAccountsReceivableAccountId: getSetting('default_accounts_receivable_account_id'),
    } : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: async (v: {
      defaultCurrency: string; fiscalYearStartMonth: string; defaultPersonnelExpenseAccountId: string; defaultPayrollLiabilityAccountId: string;
      defaultBankAccountId: string; defaultAccountsPayableAccountId: string; defaultAccountsReceivableAccountId: string;
    }) => {
      await financeApi.updateSetting('default_currency', v.defaultCurrency);
      await financeApi.updateSetting('fiscal_year_start_month', v.fiscalYearStartMonth);
      if (v.defaultPersonnelExpenseAccountId) await financeApi.updateSetting('default_personnel_expense_account_id', v.defaultPersonnelExpenseAccountId);
      if (v.defaultPayrollLiabilityAccountId) await financeApi.updateSetting('default_payroll_liability_account_id', v.defaultPayrollLiabilityAccountId);
      if (v.defaultBankAccountId) await financeApi.updateSetting('default_bank_account_id', v.defaultBankAccountId);
      if (v.defaultAccountsPayableAccountId) await financeApi.updateSetting('default_accounts_payable_account_id', v.defaultAccountsPayableAccountId);
      if (v.defaultAccountsReceivableAccountId) await financeApi.updateSetting('default_accounts_receivable_account_id', v.defaultAccountsReceivableAccountId);
    },
    onSuccess: () => { showSuccess('Finance settings saved.'); queryClient.invalidateQueries({ queryKey: ['finance-settings'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save settings.'),
  });

  const postingAccounts = accountsQuery.data?.data.filter((a) => a.posting_allowed && a.is_active) ?? [];

  return (
    <div className="page">
      <div className="page-header"><h1>Finance Settings</h1></div>
      <section className="card">
        {settingsQuery.data && (
          <form onSubmit={handleSubmit((v) => updateMutation.mutate(v))}>
            <label>Default Currency<input {...register('defaultCurrency', { required: true })} placeholder="NGN" /></label>
            <label>
              Fiscal Year Start Month
              <select {...register('fiscalYearStartMonth')}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('en-US', { month: 'long' })}</option>
                ))}
              </select>
            </label>

            <h2>Payroll Integration - Default Accounts</h2>
            <p className="muted">Required before Payroll journal generation can work - these tell Finance which real accounts to debit/credit when accruing and paying payroll.</p>
            <label>
              Default Personnel Expense Account
              <select {...register('defaultPersonnelExpenseAccountId')}>
                <option value="">- not set -</option>
                {postingAccounts.map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
              </select>
            </label>
            <label>
              Default Payroll Liability Account
              <select {...register('defaultPayrollLiabilityAccountId')}>
                <option value="">- not set -</option>
                {postingAccounts.map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
              </select>
            </label>
            <label>
              Default Bank Account
              <select {...register('defaultBankAccountId')}>
                <option value="">- not set -</option>
                {postingAccounts.map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
              </select>
            </label>

            <h2>Expenses, Payables &amp; Receivables - Default Accounts</h2>
            <p className="muted">Required before Expense Claim approval, Payable payments, or Receivable collections can generate a journal.</p>
            <label>
              Default Accounts Payable Account
              <select {...register('defaultAccountsPayableAccountId')}>
                <option value="">- not set -</option>
                {postingAccounts.map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
              </select>
            </label>
            <label>
              Default Accounts Receivable Account
              <select {...register('defaultAccountsReceivableAccountId')}>
                <option value="">- not set -</option>
                {postingAccounts.map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
              </select>
            </label>

            <div className="form-actions"><button type="submit" className="btn-primary" disabled={updateMutation.isPending}>{updateMutation.isPending ? 'Saving…' : 'Save Settings'}</button></div>
          </form>
        )}
      </section>
    </div>
  );
}
